import { Navigate, Route, Routes } from 'react-router-dom';

import { paths } from './paths';
import { RequireAuth, RequireEmployerAdmin, PayIntentEntry } from './guards';

import { SplashPage } from '@/pages/splash';
import { AuthLandingPage } from '@/pages/auth-landing';
import { LoginEmailPage } from '@/pages/login-email';
import { LoginOtpPage } from '@/pages/login-otp';
import { OAuthGoogleCallbackPage } from '@/pages/oauth-google-callback';
import { OAuthFacebookCallbackPage } from '@/pages/oauth-facebook-callback';

import { OnboardingProfilePage } from '@/pages/onboarding/profile-basics';
import { OnboardingPassportPage } from '@/pages/onboarding/passport';
import { OnboardingNationalityPickerPage } from '@/pages/onboarding/nationality-picker';
import { OnboardingFillLaterPage } from '@/pages/onboarding/fill-later-gate';
import { OnboardingFamilyEmptyPage } from '@/pages/onboarding/family-empty';
import { OnboardingFamilyFormPage } from '@/pages/onboarding/family-form';
import { OnboardingFamilyListPage } from '@/pages/onboarding/family-list';
import { OnboardingFamilyRolePage } from '@/pages/onboarding/family-role';
import { OnboardingPaymentEmptyPage } from '@/pages/onboarding/payment-empty';
import { OnboardingPaymentCardsPage } from '@/pages/onboarding/payment-cards';
import { OnboardingEvacuationPage } from '@/pages/onboarding/evacuation-setup';
import { OnboardingEvacuationCountriesPage } from '@/pages/onboarding/evacuation-countries';
import { OnboardingSubscribePitchPage } from '@/pages/onboarding/subscribe-pitch';
import { OnboardingAllSetPage } from '@/pages/onboarding/all-set';

import { HomePage } from '@/pages/home';
import { NewsFeedPage } from '@/pages/home-news-feed';
import { NewsArticlePage } from '@/pages/home-news-article';
import { BookingOptionsPage } from '@/pages/booking-options';
import { BoardingPassPage } from '@/pages/boarding-pass';
import { BoardingActionsPage } from '@/pages/boarding-actions';
import { WalletPage } from '@/pages/wallet';
import { WalletTopupPage } from '@/pages/wallet-topup';
import { SubscribePlansPage } from '@/pages/subscribe-plans';
import { SubscribePaymentPage } from '@/pages/subscribe-payment';
import { SubscribeAddCardPage } from '@/pages/subscribe-add-card';
import { SubscribeResultPage } from '@/pages/subscribe-result';
import { PayIntentPage } from '@/pages/pay-intent';
import { MoneyLinkLandingPage } from '@/pages/money-link-landing';
import { TravelListPage } from '@/pages/travel-list';
import { TravelDetailPage } from '@/pages/travel-detail';
import { TravelFlightSearchPage } from '@/pages/travel-flight-search';
import { NotificationsPage } from '@/pages/notifications';
import { SettingsPage } from '@/pages/settings';
import { SettingsPriorityEditPage } from '@/pages/settings-priority-edit';
import { SettingsFamilyEditPage } from '@/pages/settings-family-edit';
import { SettingsPassportEditPage } from '@/pages/settings-passport-edit';
import { SettingsLanguagePage } from '@/pages/settings-language';
import { SettingsPoliciesPage } from '@/pages/settings-policies';
import { SettingsPolicyViewPage } from '@/pages/settings-policy-view';
import { SettingsEvacuationPage } from '@/pages/settings-evacuation';
import { SupportListPage } from '@/pages/support-list';
import { SupportNewPage } from '@/pages/support-new';
import { SupportTicketPage } from '@/pages/support-ticket';
import { BusinessEmptyPage } from '@/pages/business-empty';
import { BusinessCreatePage } from '@/pages/business-create';
import { BusinessSearchPage } from '@/pages/business-search';
import { BusinessCabinetPage } from '@/pages/business-cabinet';
import { BusinessDetailPage } from '@/pages/business-detail';
import { NotFoundPage } from '@/pages/not-found';
import { ErrorPage } from '@/pages/error';

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* Public */}
      <Route path={paths.splash} element={<SplashPage />} />
      <Route path={paths.authLanding} element={<AuthLandingPage />} />
      <Route path={paths.loginEmail} element={<LoginEmailPage />} />
      <Route path={paths.loginOtp} element={<LoginOtpPage />} />
      <Route path={paths.oauthGoogleCallback} element={<OAuthGoogleCallbackPage />} />
      <Route path={paths.oauthFacebookCallback} element={<OAuthFacebookCallbackPage />} />
      <Route path={paths.moneyLink()} element={<MoneyLinkLandingPage />} />

      {/* Onboarding (auth required) */}
      <Route
        path={paths.onboardingProfile}
        element={<RequireAuth><OnboardingProfilePage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingPassport}
        element={<RequireAuth><OnboardingPassportPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingNationalityPicker}
        element={<RequireAuth><OnboardingNationalityPickerPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingFillLater}
        element={<RequireAuth><OnboardingFillLaterPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingFamilyEmpty}
        element={<RequireAuth><OnboardingFamilyEmptyPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingFamilyForm}
        element={<RequireAuth><OnboardingFamilyFormPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingFamilyRole}
        element={<RequireAuth><OnboardingFamilyRolePage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingFamilyList}
        element={<RequireAuth><OnboardingFamilyListPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingPaymentEmpty}
        element={<RequireAuth><OnboardingPaymentEmptyPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingPaymentCards}
        element={<RequireAuth><OnboardingPaymentCardsPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingEvacuation}
        element={<RequireAuth><OnboardingEvacuationPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingEvacuationCountries}
        element={<RequireAuth><OnboardingEvacuationCountriesPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingSubscribePitch}
        element={<RequireAuth><OnboardingSubscribePitchPage /></RequireAuth>}
      />
      <Route
        path={paths.onboardingAllSet}
        element={<RequireAuth><OnboardingAllSetPage /></RequireAuth>}
      />

      {/* Main app (auth required) */}
      <Route path={paths.home} element={<RequireAuth><HomePage /></RequireAuth>} />
      <Route path={paths.newsFeed} element={<RequireAuth><NewsFeedPage /></RequireAuth>} />
      <Route path={paths.newsArticle()} element={<RequireAuth><NewsArticlePage /></RequireAuth>} />
      <Route path={paths.bookingOptions()} element={<RequireAuth><BookingOptionsPage /></RequireAuth>} />
      <Route path={paths.boardingPass()} element={<RequireAuth><BoardingPassPage /></RequireAuth>} />
      <Route path={paths.boardingActions()} element={<RequireAuth><BoardingActionsPage /></RequireAuth>} />

      <Route path={paths.wallet} element={<RequireAuth><WalletPage /></RequireAuth>} />
      <Route path={paths.walletTopup} element={<RequireAuth><WalletTopupPage /></RequireAuth>} />
      <Route
        path={paths.payIntent()}
        element={
          <PayIntentEntry>
            <PayIntentPage />
          </PayIntentEntry>
        }
      />
      <Route path={paths.subscribePlans} element={<RequireAuth><SubscribePlansPage /></RequireAuth>} />
      <Route path={paths.subscribePayment()} element={<RequireAuth><SubscribePaymentPage /></RequireAuth>} />
      <Route path={paths.subscribeAddCard} element={<RequireAuth><SubscribeAddCardPage /></RequireAuth>} />
      <Route path={paths.subscribeSuccess} element={<RequireAuth><SubscribeResultPage variant="success" /></RequireAuth>} />
      <Route path={paths.subscribeError} element={<RequireAuth><SubscribeResultPage variant="error" /></RequireAuth>} />

      <Route path={paths.travel} element={<RequireAuth><TravelListPage /></RequireAuth>} />
      <Route path={paths.travelDetail()} element={<RequireAuth><TravelDetailPage /></RequireAuth>} />
      <Route path={paths.travelFlightSearch} element={<RequireAuth><TravelFlightSearchPage /></RequireAuth>} />

      <Route path={paths.notifications} element={<RequireAuth><NotificationsPage /></RequireAuth>} />

      <Route path={paths.business} element={<RequireAuth><BusinessEmptyPage /></RequireAuth>} />
      <Route path={paths.businessCreate} element={<RequireAuth><BusinessCreatePage /></RequireAuth>} />
      <Route path={paths.businessSearch} element={<RequireAuth><BusinessSearchPage /></RequireAuth>} />
      <Route path={paths.businessCabinet} element={<RequireAuth><BusinessCabinetPage /></RequireAuth>} />
      <Route
        path={paths.businessDetail()}
        element={
          <RequireAuth>
            <RequireEmployerAdmin>
              <BusinessDetailPage />
            </RequireEmployerAdmin>
          </RequireAuth>
        }
      />

      <Route path={paths.settings} element={<RequireAuth><SettingsPage /></RequireAuth>} />
      <Route path={paths.settingsPriorityEdit} element={<RequireAuth><SettingsPriorityEditPage /></RequireAuth>} />
      <Route path={paths.settingsFamilyEdit} element={<RequireAuth><SettingsFamilyEditPage /></RequireAuth>} />
      <Route path={paths.settingsPassportEdit} element={<RequireAuth><SettingsPassportEditPage /></RequireAuth>} />
      <Route path={paths.settingsLanguage} element={<RequireAuth><SettingsLanguagePage /></RequireAuth>} />
      <Route path={paths.settingsPolicies} element={<RequireAuth><SettingsPoliciesPage /></RequireAuth>} />
      <Route path={paths.settingsPolicyView()} element={<RequireAuth><SettingsPolicyViewPage /></RequireAuth>} />
      <Route path={paths.settingsEvacuation} element={<RequireAuth><SettingsEvacuationPage /></RequireAuth>} />

      <Route path={paths.support} element={<RequireAuth><SupportListPage /></RequireAuth>} />
      <Route path={paths.supportNew} element={<RequireAuth><SupportNewPage /></RequireAuth>} />
      <Route path={paths.supportTicket()} element={<RequireAuth><SupportTicketPage /></RequireAuth>} />

      <Route path={paths.error} element={<ErrorPage />} />
      <Route path={paths.notFound} element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to={paths.notFound} replace />} />
    </Routes>
  );
}
